import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { useCallback, useContext } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'
import { WretchError } from 'wretch/resolver'
import { type z } from 'zod'

import { RegisterCaseDialogContext } from '@/pages/_app/applicants/context'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { editCategoryMutation } from '@/lib/queries/business/v1/case/path/mutations'
import { getCategoryListByApplicantQuery } from '@/lib/queries/business/v1/case/path/queries'
import { getCompanyQuery } from '@/lib/queries/business/v1/company/queries'
import { type ResCategory } from '@/lib/queries/business/v1/types'
import { getMembersQuery } from '@/lib/queries/business/v1/user/queries'
import { createCaseMutation } from '@/lib/queries/business/v2/case/register/mutations'
import { getUserQuery } from '@/lib/queries/business/v2/user/queries'
import { isApiErrorResponse } from '@/lib/types/predicate'

import { defaultCategories } from '../../../constants'
import { processTags } from '../utils/process-tags'
import { formSchema } from './validation'

interface Props {
  onRegistered: () => void
}

export const useRegisterUscisAndDolCase = ({ onRegistered }: Props) => {
  const queryClient = useQueryClient()
  const { applicantId } = useParams({
    from: '/_app/applicants/$applicantId',
  })
  const { closeDialog } = useContext(RegisterCaseDialogContext)

  const { data: dataListCategory } = useQuery(getCategoryListByApplicantQuery({ applicantId }))
  const { data: dataUser } = useQuery(getUserQuery)
  const { data: dataMembers } = useQuery(getMembersQuery)
  const { data: dataCompany } = useQuery(getCompanyQuery)
  const { mutateAsync: createCategoryAsync, isPending: isPendingCreateCategory } = useMutation(editCategoryMutation)
  const { mutate: createCase, isPending: isPendingCreateCase } = useMutation(createCaseMutation)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      assignees: [],
      categoryName: '',
      caseNumber: '',
      tags: '',
    },
  })
  const formErrors = form.formState.errors
  const categoryId = useWatch({ name: 'categoryId', control: form.control })
  const caseNumber = useWatch({
    name: 'caseNumber',
    control: form.control,
  })
  const categoryName = useWatch({ name: 'categoryName', control: form.control })

  const userId = dataUser?.user.id
  const assignees = dataMembers?.members
    ? dataMembers.members?.map(({ id, name }) => ({
        label: `${name}${id === userId ? ' (Owner)' : ''}`,
        value: `${id}`,
        disabled: id === userId,
      }))
    : []
  const defaultAssignees =
    userId && dataMembers?.members
      ? dataCompany?.defaultAssigneeAll
        ? dataMembers.members.map(({ id }) => `${id}`)
        : [`${userId}`]
      : []

  const isDisabledSubmitButton =
    !categoryId ||
    !caseNumber ||
    categoryId === 'add-category' ||
    !!formErrors.categoryId ||
    !!formErrors.caseNumber ||
    isPendingCreateCategory ||
    isPendingCreateCase

  const handleCategoryNameSave = useCallback(async () => {
    if (!categoryName.trim()) {
      return
    }

    const categories = await createCategoryAsync({
      applicantId: Number(applicantId),
      categoryName,
    })
    await queryClient.invalidateQueries({
      queryKey: [API_ENDPOINTS.business.v1.case.path.index],
    })

    const newCategory = categories[categories.length - 1]

    form.setValue('categoryId', String(newCategory.id))
    form.resetField('categoryName')
    await form.trigger('categoryId')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantId, categoryName])

  const handleSubmit = form.handleSubmit(async (data) => {
    const tags = [...new Set(processTags(data.tags))]

    /*
     * if user selects a new category,
     * request for creating new category first then registering a new case
     */
    let newCategory: ResCategory | undefined
    if (data.categoryId.startsWith('_')) {
      const selectedCategory = defaultCategories.find((x) => x.id === data.categoryId)
      if (selectedCategory) {
        const categories = await createCategoryAsync({
          applicantId: Number(applicantId),
          categoryName: selectedCategory.label,
        })

        await queryClient.invalidateQueries({
          queryKey: [API_ENDPOINTS.business.v1.case.path.index],
        })

        newCategory = categories[categories.length - 1]
      }
    }

    createCase(
      {
        case_number: data.caseNumber,
        assignee: data.assignees?.map((userId) => Number(userId)),
        path_id: newCategory ? newCategory.id : Number(categoryId),
        tags,
      },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries(getUserQuery),
            queryClient.invalidateQueries({
              queryKey: [API_ENDPOINTS.business.v1.case.path.index],
            }),
            queryClient.invalidateQueries({
              queryKey: [API_ENDPOINTS.business.v4.case.search.index],
            }),
          ])

          toast.success('Case has been successfully registered.')

          form.reset()
          onRegistered()
          closeDialog()
        },
        onError: (e) => {
          if (e instanceof WretchError && isApiErrorResponse(e.json)) {
            toast.error(e.json.description)
            return
          }
          toast.error('Failed to register case by unknown error.')
        },
      }
    )
  })

  return {
    applicantId,
    dataListCategory,
    form,
    formErrors,
    categoryId,
    categoryName,
    assignees,
    defaultAssignees,
    isPendingCreateCategory,
    isDisabledSubmitButton,
    handleCategoryNameSave,
    handleSubmit,
  }
}
